/**
 * Login.tsx — mango-web
 * PURPOSE: Full sign-in screen mirroring Android's SignInScreen.kt.
 * Split-screen layout: form left, illustration right. No scroll.
 * Features: email+password, magic link, Google OAuth, Apple OAuth,
 * forgot password modal, sign-up link. Email validation with shake.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { ForgotPasswordModal } from './ForgotPasswordModal';

// --- Helpers ---

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// --- Component ---

/**
 * PURPOSE: Render the split-screen sign-in page with all auth methods.
 * - Connected to: stores/authStore.ts → all sign-in actions
 * - Connected to: routes/auth/ForgotPasswordModal.tsx → password reset
 * - Used by: App.tsx → route "/login"
 */
export function Login() {
  const navigate = useNavigate();
  const {
    user, isLoading, error, magicLinkSent,
    signInWithEmail, signInWithMagicLink,
    signInWithGoogle, signInWithApple,
    clearError, clearMagicLinkSent,
  } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [shake, setShake] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  console.debug('[Login] mounted');

  // --- Redirect if already authenticated ---
  useEffect(() => {
    if (user) {
      console.debug('[Login] user authenticated, redirecting to /');
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // --- Validation ---
  const isValidEmail = EMAIL_REGEX.test(email);
  const emailError = emailTouched && email.length > 0 && !isValidEmail
    ? 'please enter a valid email.' : '';
  const canSubmit = isValidEmail && password.length > 0 && !isLoading;

  // --- Handlers ---

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const ok = await signInWithEmail(email, password);
    if (ok) navigate('/', { replace: true });
  };

  const handleMagicLink = async () => {
    if (!email.trim() || !isValidEmail) {
      setEmailTouched(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      emailRef.current?.focus();
      return;
    }
    clearError();
    await signInWithMagicLink(email);
  };

  // --- Magic link sent state ---
  if (magicLinkSent) {
    return (
      <div className="auth-split">
        <div className="auth-form-side">
          <div className="auth-form-inner auth-magic-sent">
            <div className="magic-sent-icon">✉️</div>
            <h2 className="auth-title font-display">check your email</h2>
            <p className="auth-subtitle">
              we sent a sign-in link to <strong>{email}</strong>
            </p>
            <button className="ghost-link" onClick={() => clearMagicLinkSent()}>
              back to sign in
            </button>
          </div>
        </div>
        <div className="auth-illust-side">
          <img src="/auth-signin.svg" alt="" className="auth-illust-img" />
          <div className="auth-illust-overlay" />
        </div>
      </div>
    );
  }

  // --- Main form ---
  return (
    <div className="auth-split">
      {/* Left — Form */}
      <div className="auth-form-side">
        <div className="auth-form-inner">

          {/* Brand */}
          <div className="auth-brand">
            <img src="/mango-logo.png" alt="mango" className="auth-logo-img" />
            <h1 className="auth-brand-name font-display">mango</h1>
            <p className="auth-tagline font-mono">TWILIO CALLS · MESSAGES</p>
          </div>

          {/* Title */}
          <h2 className="auth-title font-display">sign in</h2>

          {/* Form */}
          <form onSubmit={handleSignIn} className="auth-form">
            <div className="input-group">
              <input
                ref={emailRef}
                type="email"
                className={`auth-input ${shake ? 'shake' : ''} ${emailError ? 'auth-input-error' : ''}`}
                placeholder="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (!emailTouched && e.target.value.length > 0) setEmailTouched(true);
                }}
                autoComplete="email"
                autoFocus
              />
              {emailError && <span className="input-error-text">{emailError}</span>}
            </div>

            <div className="input-group">
              <div className="input-password-wrap">
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  className="auth-input"
                  placeholder="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  aria-label={passwordVisible ? 'hide password' : 'show password'}
                >
                  {passwordVisible ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {error && <div className="auth-error">{error}</div>}

            <button type="submit" className="volt-btn" disabled={!canSubmit}>
              <span className="btn-label">
                {isLoading ? 'signing in...' : 'sign in'}
              </span>
            </button>
          </form>

          {/* Divider */}
          <div className="auth-divider">
            <span className="auth-divider-line" />
            <span className="auth-divider-text font-mono">OR</span>
            <span className="auth-divider-line" />
          </div>

          {/* OAuth */}
          <div className="auth-options">
            <button className="auth-option-btn" onClick={signInWithGoogle}>
              <svg className="auth-option-icon" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              <span>sign in with google</span>
            </button>

            <button className="auth-option-btn" onClick={signInWithApple}>
              <svg className="auth-option-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
              <span>sign in with apple</span>
            </button>

            <button className="auth-option-btn" onClick={handleMagicLink} disabled={isLoading}>
              <span className="auth-option-icon-emoji">✨</span>
              <span>{isLoading ? 'sending...' : 'sign in with magic link'}</span>
            </button>
          </div>

          {/* Footer */}
          <div className="auth-footer-links">
            <p className="auth-footer-row">
              <span className="auth-footer-muted">forgot password?</span>
              <button className="auth-footer-link" onClick={() => setShowForgotPassword(true)}>
                reset now
              </button>
            </p>
            <p className="auth-footer-row">
              <span className="auth-footer-muted">don't have an account?</span>
              <Link to="/signup" className="auth-footer-link">sign up</Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right — Illustration */}
      <div className="auth-illust-side">
        <img src="/auth-signin.svg" alt="" className="auth-illust-img" />
        <div className="auth-illust-overlay" />
      </div>

      {/* Modal */}
      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
    </div>
  );
}

// --- Related Files ---
// CONNECTED TO:
//   - src/stores/authStore.ts — all auth actions
//   - src/routes/auth/ForgotPasswordModal.tsx — password reset modal
//   - src/styles/auth.css — split-screen layout styles
// USED BY:
//   - src/App.tsx — route "/login"
